import { FeatureFlag } from '@ps/web-core-store';
import type { Selector } from '@rematch/select';

import type {
  EffectParams,
  ModelConfig,
  RootState,
  SelectorCreator,
  Slicer
} from '@swf/core/store';
import { select } from '@swf/core/store';

import type { ExtraWallet } from './types/extraWallets';
import { getDisplayableWallets } from './utils/extraWallets/getDisplayableWallets';

export interface ExtraWalletsState {
  wallets: ExtraWallet[];
  loading: boolean;
}

export interface ExtraWalletsResponse {
  extraWallets: ExtraWallet[];
}

interface ExtraWalletsModel extends ModelConfig<ExtraWalletsState> {
  reducers: typeof reducers;
  effects: typeof effects;
  selectors: typeof selectors;
}

const reducers = {
  walletsLoadedSuccessfully: (
    state: ExtraWalletsState,
    payload: ExtraWalletsResponse
  ): ExtraWalletsState => ({
    ...state,
    wallets: payload.extraWallets.filter((wallet) => wallet.amount > 0),
    loading: false
  }),
  'session/clear': (state: ExtraWalletsState): ExtraWalletsState => ({
    ...state,
    wallets: []
  }),
  requestExtraWallets: (state: ExtraWalletsState): ExtraWalletsState => ({
    ...state,
    loading: true
  }),
  dataLoadFailed: (state: ExtraWalletsState): ExtraWalletsState => ({
    ...state,
    loading: false
  })
};

const effects = {
  walletsRequested: async (payload: undefined, { dispatch, request, rootState }: EffectParams) => {
    if (rootState.extraWallets.loading) return;

    dispatch.extraWallets.requestExtraWallets();

    try {
      const { body } = await request<ExtraWalletsResponse>({
        service: '/api/v1-preview/wallet/extra-wallets',
        http: true,
        method: 'POST'
      });

      if (!body) return;

      dispatch.extraWallets.walletsLoadedSuccessfully(body);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
      dispatch.extraWallets.dataLoadFailed();
    }
  }
};

const selectors = (slice: Slicer<ExtraWalletsState>, createSelector: SelectorCreator) => ({
  getAllEnabledWallets() {
    return createSelector(
      (rootState: RootState) => select.feature.check(rootState, FeatureFlag.ISPBonusCash),
      (rootState: RootState) => select.feature.check(rootState, FeatureFlag.ISPSportsFreeBets),
      slice as Selector<RootState, ExtraWalletsState>,
      (isBonusCashEnabled, isFreeBetsEnabled, state) =>
        getDisplayableWallets(state.wallets, isBonusCashEnabled, isFreeBetsEnabled)
    );
  },
  getWalletsByAccountCurrency(this: { getAllEnabledWallets: Selector<RootState, ExtraWallet[]> }) {
    return createSelector(
      this.getAllEnabledWallets,
      (rootState: RootState) => select.account.getCurrency(rootState),
      (wallets, accountCurrency) => wallets.filter((wallet) => wallet.currency === accountCurrency)
    );
  },
  getBonusCashWallets(this: { getAllEnabledWallets: Selector<RootState, ExtraWallet[]> }) {
    return createSelector(this.getAllEnabledWallets, (wallets) =>
      wallets.filter((wallet) => wallet.awardingType === 'REDEEMABLE')
    );
  },
  getFreeBetsWallets(this: { getAllEnabledWallets: Selector<RootState, ExtraWallet[]> }) {
    return createSelector(this.getAllEnabledWallets, (wallets) =>
      wallets.filter((wallet) => wallet.awardingType === 'NON_REDEEMABLE')
    );
  },
  getTotalBonusCashBalance(this: { getBonusCashWallets: Selector<RootState, ExtraWallet[]> }) {
    return createSelector(this.getBonusCashWallets, (wallets) =>
      wallets.reduce((acc, item) => acc + item.amount, 0)
    );
  },
  getTotalFreeBetsBalance(this: { getFreeBetsWallets: Selector<RootState, ExtraWallet[]> }) {
    return createSelector(this.getFreeBetsWallets, (wallets) =>
      wallets.reduce((acc, item) => acc + item.amount, 0)
    );
  },
  getExtraWalletsCurrency(this: { getAllEnabledWallets: Selector<RootState, ExtraWallet[]> }) {
    return createSelector(this.getAllEnabledWallets, (wallets) => wallets[0].currency);
  }
});

const extraWallets: ExtraWalletsModel = {
  state: {
    wallets: [],
    loading: false
  },
  reducers,
  effects,
  selectors
};

export default extraWallets;
